'use client';
import { useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { ScoredStock, isBankLike, PILLAR_FACTORS } from '@/lib/compute';
import { ScoreRing, TrendArrow } from './stock-detail/ScoreRing';
import { type FactorDetailData } from './stock-detail/FactorDetailPanel';
import { PriceChart } from './stock-detail/PriceChart';
import { SelfPercentileRow } from './stock-detail/SelfPercentileRow';
import { KeyDriversPanel } from './stock-detail/KeyDriversPanel';
import { FactorBreakdown } from './stock-detail/FactorBreakdown';
import { useStockDetailDerivations } from './stock-detail/use-stock-detail-derivations';
import { getSectorColor } from './ControlPanel';
import { ScoreBreakdownPopover } from './ScoreBreakdownPopover';
import { valuationVerbal, qualityVerbal, prodQuality } from '@/lib/composite-score';
import { fmtMkt, type PricePoint, type ProfileData } from '@/lib/stock-detail-helpers';
import { TargetPriceModal, type TargetPriceDerivation } from './stock-detail/TargetPriceModal';

interface TrendResult { direction: 'up' | 'down' | 'flat'; slope: number | null }
interface QualityTrendData {
  snapshotCount: number;
  dateRange: { from: string; to: string };
  trends: {
    quality: TrendResult;
    valuation: TrendResult;
    overall: 'up' | 'down' | 'flat';
    pillars: Record<string, TrendResult>;
  };
}

interface TechnicalData {
  shortTerm: number | null;
  longTerm: number | null;
  combined: number | null;
  dailyRsi: number | null;
  weeklyRsi: number | null;
  monthlyRsi: number | null;
  rsiSum: number | null;
  dailyStochRsi: number | null;
  dailyMacdHistogram: number | null;
  dailyMacdLine: number | null;
  dailySignalLine: number | null;
  monthlyStochRsi: number | null;
  monthlyMacdHistogram: number | null;
  monthlyMacdLine: number | null;
  monthlySignalLine: number | null;
}

interface Props {
  stock: ScoredStock;
  allStocks: ScoredStock[];
  pinnedSymbols: Set<string>;
  onTogglePin: (symbol: string) => void;
  onClose: () => void;
  technicalData?: TechnicalData;
  noBackdrop?: boolean;
  isAlphaZone?: boolean;
}

// technicalData prop is intentionally kept — fetch pipeline still relies on it.
export function StockDetail({ stock, allStocks, pinnedSymbols, onTogglePin, onClose, technicalData: _technicalData, noBackdrop, isAlphaZone }: Props) {
  const [breakdownAxis, setBreakdownAxis] = useState<'quality' | 'valuation' | null>(null);
  const [showFvModal, setShowFvModal] = useState(false);
  const [show3YrModal, setShow3YrModal] = useState(false);
  const [insiderExpanded, setInsiderExpanded] = useState(false);
  const [expandedFactor, setExpandedFactor] = useState<string | null>(null);

  // Alpha-entry only fetches when the stock IS in the Alpha Zone.
  const { data: alphaEntryData } = useResource<{ entryDate?: string | null }>(
    isAlphaZone ? `/api/alpha-entry?symbol=${stock.symbol}` : null,
  );
  const alphaEntryDate = alphaEntryData?.entryDate ?? null;

  // Factor detail fetches when the user expands a factor pill.
  const { data: factorDetail, loading: factorDetailLoading } = useResource<FactorDetailData>(
    expandedFactor ? `/api/factor-detail?symbol=${stock.symbol}` : null,
  );

  const isPinned = pinnedSymbols.has(stock.symbol);
  const sectorColor = getSectorColor(stock.sector);
  const isBank = isBankLike(stock);
  const pillarMapping = isBank ? PILLAR_FACTORS.financial : PILLAR_FACTORS.nonFinancial;

  // % forward per pillar for the pillar header (e.g. "Profitability 15% · 50%fwd").
  const isFwdKey = (k: string): boolean =>
    k.startsWith('fwd') || k.startsWith('next') || k === 'pegRatio' || k === 'impliedBuybackYield';
  const fwdPctByPillar: Record<string, number | null> = {};
  for (const [pillar, factors] of Object.entries(pillarMapping)) {
    const total = factors.length;
    if (total === 0) { fwdPctByPillar[pillar] = null; continue; }
    const fwd = factors.filter((f: { key: string }) => isFwdKey(f.key)).length;
    fwdPctByPillar[pillar] = Math.round((fwd / total) * 100);
  }

  // Reset expandable child state when the focused stock changes. Using the
  // React-recommended "set state during render on prop change" pattern instead
  // of useEffect — avoids the cascading-render lint warning.
  const [prevSymbol, setPrevSymbol] = useState(stock.symbol);
  if (prevSymbol !== stock.symbol) {
    setPrevSymbol(stock.symbol);
    setInsiderExpanded(false);
    setExpandedFactor(null);
  }

  // Prices stream — useResource caches per-symbol URL.
  const { data: pricesRaw, loading: priceLoading } = useResource<{ prices?: PricePoint[] }>(`/api/prices?symbol=${stock.symbol}`);
  const prices = pricesRaw?.prices ?? null;

  // Profile + quality-trend lookups — derived at render time (no mirror useState).
  const { data: profileRaw } = useResource<ProfileData & { error?: unknown }>(`/api/profile?symbol=${stock.symbol}`);
  const profile: ProfileData | null = profileRaw && !profileRaw.error ? profileRaw : null;

  const { data: qualityTrendRaw } = useResource<QualityTrendData & { trends?: unknown }>(`/api/quality-trend?symbol=${encodeURIComponent(stock.symbol)}`);
  const qualityTrend: QualityTrendData | null = qualityTrendRaw?.trends ? qualityTrendRaw : null;

  // Absolute valuation lenses — universe-z signals (asymmetry / DCF discount /
  // today-FV discount) that score into V_blended at 25% via vAbsoluteC.
  // Live outside PILLAR_FACTORS so they aren't in stock.factors; we pull them
  // from /api/target-prices and pass to FactorBreakdown. Cached via useResource
  // so multiple StockDetail instances share one fetch.
  const { data: targetPricesRaw } = useResource<{ stocks?: Array<{
    symbol: string;
    asymZ?: number | null;
    dcfDiscZ?: number | null;
    todayDiscZ?: number | null;
    asymmetry?: number | null;
    dcfFairValue?: number | null;
    todayFairValue?: number | null;
    threeYearTarget?: number | null;
    fiveYearTarget?: number | null;
    currentPrice?: number | null;
    vAbsoluteC?: number | null;
    // Sell-side consensus (joined from data/analyst-consensus.json server-side).
    // Display-only — NOT in scoring math. See plan inherited-singing-sky.md.
    analystTarget?: number | null;
    analystRating?: number | null;
    analystBuy?: number | null;
    analystHold?: number | null;
    analystSell?: number | null;
    analystCount?: number | null;
    // 3yr derivation fields
    fy1Eps?: number | null;
    fy3Eps?: number | null;
    targetMultiple?: { peerMedian?: number | null; ownMedian?: number | null; nearTerm?: number | null } | null;
    growth?: { gLtg?: number | null; g3y?: number | null; g5y?: number | null; median?: number | null; final?: number | null; qualityFade?: number | null } | null;
    calibrated3Y?: number | null;
  }> }>('/api/target-prices');
  const absoluteLens = (() => {
    const row = targetPricesRaw?.stocks?.find(r => r.symbol === stock.symbol);
    if (!row) return null;
    return {
      asymZ: row.asymZ ?? null,
      dcfDiscZ: row.dcfDiscZ ?? null,
      todayDiscZ: row.todayDiscZ ?? null,
      asymmetry: row.asymmetry ?? null,
      dcfFairValue: row.dcfFairValue ?? null,
      todayFairValue: row.todayFairValue ?? null,
      fiveYearTarget: row.fiveYearTarget ?? null,
      currentPrice: row.currentPrice ?? null,
      vAbsoluteC: row.vAbsoluteC ?? null,
    };
  })();

  // Derivations: peer filtering, factor stats, z-score/percentile helpers, ranks.
  const {
    peerStocks, peerGroupLabel, getZScore, getPercentile, scoreColor,
    qualityRank, valuationRank, compositeRank, compositeForStock,
  } = useStockDetailDerivations(stock, allStocks);

  return (
    <>
      {!noBackdrop && <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />}

      <div className={`fixed z-50 flex items-start justify-center overflow-y-auto pt-8 pb-8 ${noBackdrop ? 'inset-y-4 left-4 right-[55%]' : 'inset-4 right-[40%]'}`}>
        <div className="bg-[#0d1117] border border-[#1e293b] rounded-2xl w-full max-w-[900px] shadow-2xl flex flex-col max-h-[calc(100vh-4rem)]">

          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-[#1e293b] flex-shrink-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center">
                    <span className="text-white font-bold text-2xl" style={{ fontFamily: 'var(--font-mono)' }}>
                      {stock.symbol}
                    </span>
                    {qualityTrend && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[9px] text-[#94a3b8] uppercase tracking-wide">Q</span>
                        <TrendArrow direction={qualityTrend.trends.quality.direction} size="sm" />
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                    style={{ color: sectorColor, backgroundColor: `${sectorColor}15`, border: `1px solid ${sectorColor}30` }}>
                    {stock.sector}
                  </span>
                  {profile?.industry && (
                    <span className="text-[10px] text-[#e2e8f0]">{profile.industry}</span>
                  )}
                  {isAlphaZone && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold animate-pulse"
                      style={{
                        color: '#22c55e',
                        backgroundColor: '#22c55e15',
                        border: '1px solid #22c55e40',
                      }}>
                      Alpha Zone {alphaEntryDate ? `DOE ${new Date(alphaEntryDate + 'T12:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}` : ''}
                    </span>
                  )}
                </div>
                <div className="text-[#e2e8f0] text-sm mt-0.5">{stock.name}</div>
                <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                  <span className="text-white text-lg font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
                    ${stock.price.toFixed(2)}
                  </span>
                  <span className="text-[#e2e8f0] text-sm" style={{ fontFamily: 'var(--font-mono)' }}>
                    {fmtMkt(stock.marketCap)}
                  </span>
                  {profile?.ceo && (
                    <span className="text-white text-xs">CEO: {profile.ceo}</span>
                  )}
                  {profile?.fullTimeEmployees && (
                    <span className="text-white text-xs">{parseInt(profile.fullTimeEmployees).toLocaleString()} employees</span>
                  )}
                  {profile?.fyEndMonthName && (
                    <span className="text-[#94a3b8] text-xs" title={`Fiscal year ends ${profile.fyEndMonthName} 30. Earnings periods are aligned to that fiscal year, not calendar quarters.`}>
                      FY ends {profile.fyEndMonthName}
                    </span>
                  )}
                  {(profile?.latestReportedPeriod || profile?.nextReportDate) && (
                    <span className="text-xs whitespace-nowrap" title={`Last reported ${profile?.latestReportedPeriod ?? '—'}${profile?.latestReportedDate ? ` (${profile.latestReportedDate})` : ''}${profile?.nextReportDate ? ` · Next reports ${profile.nextReportDate}${profile?.nextReportDaysOut != null ? ` (${profile.nextReportDaysOut}d out)` : ''}` : ''}`}>
                      {profile?.latestReportedPeriod && (
                        <>
                          <span className="text-amber-300/90">Last reported: {profile.latestReportedPeriod}</span>
                          {profile.latestReportedDate && (
                            <span className="text-[#64748b] ml-1">({profile.latestReportedDate})</span>
                          )}
                        </>
                      )}
                      {profile?.latestReportedPeriod && profile?.nextReportDate && (
                        <span className="text-[#475569] mx-2">·</span>
                      )}
                      {profile?.nextReportDate && (
                        <>
                          <span className="text-emerald-300/90">Next reports: {profile.nextReportDate}</span>
                          {profile.nextReportDaysOut != null && (
                            <span className="text-[#64748b] ml-1">({profile.nextReportDaysOut}d)</span>
                          )}
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => onTogglePin(stock.symbol)}
                  className={`p-2 rounded-lg border transition-colors duration-150 cursor-pointer
                             ${isPinned
                               ? 'border-blue-500/50 bg-blue-500/10 text-blue-400'
                               : 'border-[#1e293b] text-white hover:text-[#e2e8f0] hover:border-[#334155]'}`}
                  title={isPinned ? 'Unpin' : 'Pin for comparison'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'}
                    stroke="currentColor" strokeWidth={2}>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
                  </svg>
                </button>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg border border-[#1e293b] text-white hover:text-[#e2e8f0]
                             hover:border-[#334155] transition-colors duration-150 cursor-pointer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

            {profile?.description && (
              <div>
                <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0] mb-1.5">About</div>
                <p className="text-[12px] text-[#e2e8f0] leading-relaxed line-clamp-4">{profile.description}</p>
              </div>
            )}

            <div>
              {priceLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <div className="w-3 h-3 border border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                  <span className="text-white text-[10px]">Loading price data...</span>
                </div>
              ) : prices && prices.length > 0 ? (
                <PriceChart allPrices={prices} />
              ) : (
                <div className="text-white text-[10px] py-2">No price data available</div>
              )}
            </div>

            {/* Unified scorecard — three Q/V/C circles above three valuation
                square tiles. Replaces the prior CompositeStack banner pills +
                SCORES section + TargetPriceSection (the 4 "ugly banners" Steve
                directive 2026-05-11). Pulls target prices from the same
                /api/target-prices row that powers the absolute lenses lower
                in the popup. */}
            <div className="border-t border-[#1e293b] pt-3">
              <div className="grid grid-cols-3 gap-6 py-1">
                <div className="flex justify-center">
                  <ScoreRing
                    score={prodQuality(stock)}
                    label="Quality"
                    rank={qualityRank}
                    peerCount={peerStocks.length}
                    onClick={() => setBreakdownAxis('quality')}
                    verbal={qualityVerbal(prodQuality(stock)).word}
                    verbalColor={qualityVerbal(prodQuality(stock)).color}
                  />
                </div>
                <div className="flex justify-center">
                  <ScoreRing
                    score={stock.valuationScore}
                    label="Valuation"
                    rank={valuationRank}
                    peerCount={peerStocks.length}
                    onClick={() => setBreakdownAxis('valuation')}
                    verbal={valuationVerbal(stock.valuationScore).word}
                    verbalColor={valuationVerbal(stock.valuationScore).color}
                  />
                </div>
                <div className="flex justify-center">
                  <ScoreRing
                    score={compositeForStock}
                    label="Composite"
                    rank={compositeRank}
                    peerCount={peerStocks.length}
                  />
                </div>
              </div>

              {/* Three valuation target squares — Fair Value Today, 3-Yr, 5-Yr.
                  Tone matches the upside vs current price (emerald ≥50% / amber
                  ≥20% / slate otherwise). Hides cleanly when no data. */}
              {(absoluteLens?.todayFairValue != null || absoluteLens?.fiveYearTarget != null) && (() => {
                const streetRow = targetPricesRaw?.stocks?.find(r => r.symbol === stock.symbol);
                const analystTarget = streetRow?.analystTarget ?? null;
                const analystRating = streetRow?.analystRating ?? null;
                const analystCount = streetRow?.analystCount ?? null;
                const buy = streetRow?.analystBuy ?? 0;
                const hold = streetRow?.analystHold ?? 0;
                const sell = streetRow?.analystSell ?? 0;
                const px = absoluteLens?.currentPrice ?? stock.price ?? null;
                const streetUpsidePct = analystTarget != null && px != null && px > 0
                  ? (analystTarget / px - 1) * 100
                  : null;
                // We show street rating + target separately rather than rolling
                // them into a single "MODEL vs STREET" verdict pill — the model's
                // 5y annualized return and the street's 1y total return have
                // different horizons, so combining them into one signal misleads.
                const ratingLabel = analystRating == null ? null
                  : analystRating >= 4.5 ? 'strong buy'
                  : analystRating >= 3.75 ? 'buy'
                  : analystRating >= 2.75 ? 'hold'
                  : analystRating >= 1.75 ? 'sell'
                  : 'strong sell';
                return <>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {(() => {
                    const tiles: Array<{ label: string; target: number | null; onClickModal: () => void }> = [
                      { label: 'Fair Value Today', target: absoluteLens?.todayFairValue ?? null, onClickModal: () => setShowFvModal(true) },
                      { label: '3-Yr Target', target: streetRow?.threeYearTarget ?? null, onClickModal: () => setShow3YrModal(true) },
                      { label: '5-Yr Target', target: absoluteLens?.fiveYearTarget ?? null, onClickModal: () => setShowFvModal(true) },
                    ];
                    const px = absoluteLens?.currentPrice ?? stock.price ?? null;
                    return tiles.map((t, i) => {
                      if (t.target == null || px == null || px <= 0) {
                        return (
                          <div key={i} className="rounded-md border border-[#1e293b] bg-[#0a0e14] px-3 py-2 text-center">
                            <div className="text-[9px] uppercase tracking-wider text-[#64748b]">{t.label}</div>
                            <div className="text-[18px] font-bold tabular-nums text-[#475569]">—</div>
                          </div>
                        );
                      }
                      const upPct = (t.target / px - 1) * 100;
                      const yrs = t.label.startsWith('5') ? 5 : t.label.startsWith('3') ? 3 : 0;
                      const annPct = yrs > 0 ? (Math.pow(t.target / px, 1 / yrs) - 1) * 100 : null;
                      const tone = upPct >= 50 ? '#10b981' : upPct >= 20 ? '#f59e0b' : upPct >= 0 ? '#94a3b8' : '#ef4444';
                      return (
                        <div
                          key={i}
                          className="rounded-md border bg-[#0a0e14] px-3 py-2 text-center cursor-pointer hover:bg-[#0d1520] transition-colors"
                          style={{ borderColor: `${tone}40` }}
                          title="Click to see full derivation"
                          onClick={t.onClickModal}
                        >
                          <div className="text-[9px] uppercase tracking-wider text-[#94a3b8]">{t.label}</div>
                          <div className="text-[18px] font-bold tabular-nums" style={{ color: tone, fontFamily: 'var(--font-mono)' }}>
                            ${t.target.toFixed(0)}
                          </div>
                          <div className="text-[10px] tabular-nums text-[#94a3b8]">
                            {upPct >= 0 ? '+' : ''}{upPct.toFixed(0)}%
                            {annPct != null && <span className="text-[#64748b]"> · {annPct >= 0 ? '+' : ''}{annPct.toFixed(1)}%/yr</span>}
                          </div>
                          <div className="text-[8px] text-[#334155] mt-0.5">tap to derive ↗</div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Sell-side consensus — Bloomberg-sourced. DISPLAY ONLY: not in
                    scoring math. Showing target + rating + distribution so the
                    PM can spot model-vs-street agreement at a glance. Triumph's
                    edge thesis: model bullish + street lukewarm = high-conviction
                    Alpha Zone setup. Hides when no analyst coverage. */}
                {analystTarget != null && (
                  <div className="mt-2 rounded-md border border-[#1e293b] bg-[#0a0e14] px-3 py-2">
                    <div className="flex items-baseline gap-4 flex-wrap">
                      <div>
                        <span className="text-[9px] uppercase tracking-wider text-[#64748b] mr-1.5">Street target (1-Yr)</span>
                        <span className="text-[13px] font-bold tabular-nums text-[#cbd5e1]" style={{ fontFamily: 'var(--font-mono)' }}>${analystTarget.toFixed(0)}</span>
                        {streetUpsidePct != null && (
                          <span className="text-[11px] tabular-nums ml-1.5" style={{ color: streetUpsidePct >= 0 ? '#94a3b8' : '#ef4444' }}>
                            {streetUpsidePct >= 0 ? '+' : ''}{streetUpsidePct.toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {analystRating != null && (
                        <div className="text-[11px] tabular-nums text-[#94a3b8]">
                          <span className="text-[9px] uppercase tracking-wider text-[#64748b] mr-1">Rating</span>
                          {analystRating.toFixed(2)}/5 <span className="text-[#64748b]">({ratingLabel})</span>
                        </div>
                      )}
                      {analystCount != null && analystCount > 0 && (
                        <div className="text-[11px] tabular-nums text-[#94a3b8]">
                          <span className="text-[9px] uppercase tracking-wider text-[#64748b] mr-1">Covers</span>
                          <span className="text-emerald-400/80">{buy}</span> buy · <span className="text-[#cbd5e1]">{hold}</span> hold · <span className="text-red-400/80">{sell}</span> sell <span className="text-[#64748b]">({analystCount})</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </>;
              })()}

              <SelfPercentileRow symbol={stock.symbol} />
            </div>

            <KeyDriversPanel
              stock={stock}
              pillarMapping={pillarMapping}
              getZScore={getZScore}
              scoreColor={scoreColor}
            />

            <FactorBreakdown
              stock={stock}
              pillarMapping={pillarMapping}
              fwdPctByPillar={fwdPctByPillar}
              peerGroupLabel={peerGroupLabel}
              getZScore={getZScore}
              getPercentile={getPercentile}
              scoreColor={scoreColor}
              expandedFactor={expandedFactor}
              setExpandedFactor={setExpandedFactor}
              factorDetail={factorDetail}
              factorDetailLoading={factorDetailLoading}
              insiderExpanded={insiderExpanded}
              setInsiderExpanded={setInsiderExpanded}
              absoluteLens={absoluteLens}
            />
          </div>
        </div>
      </div>
      {breakdownAxis && (
        <ScoreBreakdownPopover
          axis={breakdownAxis}
          stock={stock as unknown as React.ComponentProps<typeof ScoreBreakdownPopover>['stock']}
          onClose={() => setBreakdownAxis(null)}
        />
      )}
      {showFvModal && (() => {
        const row = targetPricesRaw?.stocks?.find(r => r.symbol === stock.symbol);
        if (!row) return null;
        const d: TargetPriceDerivation = row as unknown as TargetPriceDerivation;
        return <TargetPriceModal d={d} onClose={() => setShowFvModal(false)} />;
      })()}
      {show3YrModal && <ThreeYrDerivationModal symbol={stock.symbol} row={targetPricesRaw?.stocks?.find(r => r.symbol === stock.symbol) ?? null} onClose={() => setShow3YrModal(false)} />}
    </>
  );
}

function ThreeYrDerivationModal({ symbol, row, onClose }: {
  symbol: string;
  row: { threeYearTarget?: number | null; fy1Eps?: number | null; fy3Eps?: number | null; currentPrice?: number | null; targetMultiple?: unknown; growth?: unknown; calibrated3Y?: number | null; dcfFairValue?: number | null } | null;
  onClose: () => void;
}) {
  if (!row) return null;
  const tgt = row.threeYearTarget ?? null;
  const px = row.currentPrice ?? null;
  const mult = row.targetMultiple as { peerMedian?: number | null; ownMedian?: number | null; nearTerm?: number | null } | null;
  const g = row.growth as { gLtg?: number | null; g3y?: number | null; g5y?: number | null; median?: number | null; final?: number | null; qualityFade?: number | null } | null;
  const delta = tgt != null && px != null && px > 0 ? `${((tgt / px - 1) * 100) >= 0 ? '+' : ''}${((tgt / px - 1) * 100).toFixed(0)}%` : '';
  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative z-10 bg-[#0a0e14] border border-[#1e293b] rounded-xl p-6 w-[440px] shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#64748b] mb-0.5">3-Year Target · Derivation</div>
            <div className="text-[18px] font-bold text-white">{symbol} — {tgt != null ? `$${tgt.toFixed(2)}` : '—'}</div>
            {px != null && delta && <div className="text-[11px] text-[#64748b]">vs current ${px.toFixed(2)} ({delta})</div>}
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-white text-xl leading-none ml-4">×</button>
        </div>
        <div className="text-[10px] text-[#64748b] bg-[#0d1117] border border-[#1e293b] rounded px-3 py-2 mb-4 font-mono">
          3-Year Target = FY+3 EPS × Applied Multiple
          {row.fy3Eps != null && mult?.nearTerm != null && (
            <span className="ml-2 text-[#94a3b8]">= ${(row.fy3Eps).toFixed(2)} × {mult.nearTerm.toFixed(2)}× = {tgt != null ? `$${tgt.toFixed(2)}` : '—'}</span>
          )}
        </div>
        <div className="mb-4">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-2">Applied Multiple (P/E)</div>
          <div className="space-y-1">
            {mult?.peerMedian != null && <DRow label="Peer group median" value={`${mult.peerMedian.toFixed(2)}×`} />}
            {mult?.ownMedian != null && <DRow label="5yr own-history median" value={`${mult.ownMedian.toFixed(2)}×`} />}
            {mult?.nearTerm != null && <DRow label="Applied blend" value={`${mult.nearTerm.toFixed(2)}×`} hi />}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-2">EPS Growth Path → FY+3</div>
          <div className="space-y-1">
            {g?.gLtg != null && <DRow label="BBG LTG consensus" value={`${(g.gLtg * 100).toFixed(1)}%`} />}
            {g?.g3y != null && <DRow label="BBG 3yr consensus" value={`${(g.g3y * 100).toFixed(1)}%`} />}
            {g?.g5y != null && <DRow label="BBG 5yr consensus" value={`${(g.g5y * 100).toFixed(1)}%`} />}
            {g?.median != null && <DRow label="Median of above" value={`${(g.median * 100).toFixed(1)}%`} />}
            {g?.qualityFade != null && <DRow label={`Quality fade ×${g.qualityFade.toFixed(2)}`} value="" />}
            {g?.final != null && <DRow label="Final rate applied" value={`${(g.final * 100).toFixed(1)}%`} hi />}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-2">Earnings Per Share</div>
          <div className="space-y-1">
            {row.fy1Eps != null && <DRow label="FY+1 EPS (BBG 1BF)" value={`$${(row.fy1Eps).toFixed(2)}`} />}
            {row.fy3Eps != null && <DRow label="FY+3 EPS (BBG 3BF)" value={`$${(row.fy3Eps).toFixed(2)}`} hi />}
          </div>
        </div>
        <div className="border-t border-[#1e293b] pt-3 flex items-center justify-between">
          <div className="text-[10px] text-[#64748b]">3-Year Target Price</div>
          <div className="text-[18px] font-bold text-white tabular-nums">{tgt != null ? `$${tgt.toFixed(2)}` : '—'}</div>
        </div>
        {row.dcfFairValue != null && (
          <div className="mt-3 pt-3 border-t border-[#1e293b]">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-1">DCF Intrinsic Value (reference)</div>
            <div className="flex justify-between items-baseline text-[10px]">
              <span className="text-[#64748b]">10-yr DCF, discounted to today</span>
              <span className="tabular-nums font-mono text-[#64748b]">${row.dcfFairValue.toFixed(2)}{px != null && px > 0 ? ` (${((row.dcfFairValue / px - 1) * 100) >= 0 ? '+' : ''}${((row.dcfFairValue / px - 1) * 100).toFixed(0)}%)` : ''}</span>
            </div>
            <div className="text-[8px] text-[#334155] mt-1">DCF = today&apos;s intrinsic value, not a 3yr price target — shown for context only.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function DRow({ label, value, hi }: { label: string; value: string; hi?: boolean }) {
  return (
    <div className="flex justify-between items-baseline text-[10px]">
      <span className={hi ? 'text-[#94a3b8] font-semibold' : 'text-[#64748b]'}>{label}</span>
      <span className={`tabular-nums font-mono ${hi ? 'text-white font-semibold' : 'text-[#64748b]'}`}>{value}</span>
    </div>
  );
}
